/**
 * Feature Usage Utilities
 * Handles checking and recording feature usage for onboarding flows
 */

import authAxios from './authAxios';

export type FeatureType =
  | 'receipt_upload_file'
  | 'receipt_upload_email'
  | 'riw_invoice_created'
  | 'stripe_invoice_created'
  | 'riw_invoice_email_sent'
  | 'transaction_created'
  | 'plaid_account_connected'
  | 'financial_reports_viewed'
  | 'luca_chat_messages'
  | 'automation_formula_created'
  | 'tax_filing_canada_corp_diy'
  | 'tax_filing_canada_corp_cpa'
  | 'tax_filing_quebec_corp_cpa'
  | 'tax_filing_quebec_corp_diy'
  | 'tax_filing_canada_soleprop_diy'
  | 'tax_filing_canada_soleprop_cpa'
  | 'tax_filing_quebec_soleprop_cpa'
  | 'tax_filing_quebec_soleprop_diy'
  | 'tax_filing_usa_corp_diy'
  | 'tax_filing_usa_corp_cpa'
  | 'tax_filing_usa_soleprop_diy'
  | 'tax_filing_usa_soleprop_cpa';

/**
 * Get feature usage for a specific feature
 * Returns usage data or null if feature was never used
 *
 * @param companyId - The company ID
 * @param feature - The feature to check usage for
 * @returns Feature usage object with usage property, or null if not found
 */
export async function getFeatureUsage(
  companyId: string,
  feature: FeatureType
): Promise<{ usage: any } | null> {
  try {
    const axiosReq = await authAxios();

    const response = await axiosReq.get(
      `/log/feature-usage/${companyId}/${feature}`
    );

    if (!response.data) {
      throw new Error('No data in response');
    }

    // The API response is wrapped in a data property
    // response.data = { success, companyId, feature, usage }
    const apiData = response.data;

    return {
      usage: apiData.usage,
    };
  } catch (error) {
    console.error('Error getting feature usage:', error);
    return null;
  }
}

/**
 * Get onboarding task completion status for tasks that auto-complete
 * Returns status for: import-transactions, upload-receipt, send-invoice
 * Other tasks (download-app, chat-luca, view-reports) are user manual check-off only
 *
 * @param companyId - The company ID
 * @returns Object with task IDs and their completion status
 */
export async function getOnboardingTaskStatus(companyId: string): Promise<{
  'import-transactions': boolean;
  'upload-receipt': boolean;
  'send-invoice': boolean;
} | null> {
  try {
    const axiosReq = await authAxios();

    const response = await axiosReq.get(`/log/onboarding-status/${companyId}`);

    if (!response.data || !response.data.tasks) {
      throw new Error('No data in response');
    }

    return response.data.tasks;
  } catch (error) {
    console.error('Error getting onboarding task status:', error);
    return null;
  }
}

/**
 * Record feature usage for a specific feature
 *
 * @param companyId - The company ID
 * @param feature - The feature to record usage for
 */
export async function recordFeatureUsage(
  companyId: string,
  feature: FeatureType
): Promise<boolean> {
  try {
    const axiosReq = await authAxios();

    await axiosReq.post(`/log/record-feature-usage/${companyId}`, {
      feature,
    });

    return true;
  } catch (error) {
    console.error('Error recording feature usage:', error);
    return false;
  }
}

/**
 * Check if transactions exist and record feature usage if they do
 * This reuses the same logic as the transaction first-time view
 *
 * @param companyId - The company ID
 * @returns Boolean indicating if transaction_created feature exists or was just created
 */
export async function checkTransactionUsage(
  companyId: string
): Promise<boolean> {
  try {
    const axiosReq = await authAxios();

    // Call the backend endpoint to check and record transaction usage
    // This endpoint checks for existing feature_usage document first,
    // then checks if transactions exist, and creates the document if needed
    await axiosReq.post(`/log/check-transaction-usage/${companyId}`);

    // Then check if the feature has been used (will be true if document exists or was just created)
    const featureResponse = await getFeatureUsage(
      companyId,
      'transaction_created'
    );

    return !!featureResponse?.usage;
  } catch (error) {
    console.error('Error checking transaction usage:', error);
    return false;
  }
}

/**
 * Get company onboarding state (task completions and card state)
 * Returns onboarding task completions (0 or 1) and card state (open, minimized, closed)
 *
 * @param companyId - The company ID
 * @returns Object with tasks and cardState
 */
export async function getCompanyOnboardingState(companyId: string): Promise<{
  tasks: {
    'import-transactions': number;
    'send-invoice': number;
    'upload-receipt': number;
    'chat-luca': number;
    'view-reports': number;
    'download-app': number;
  };
  cardState: 'open' | 'minimized' | 'closed';
  confettiShown?: boolean; // Flag to track if confetti celebration has been shown
  isFirstTimeUser?: boolean; // Flag to indicate if user is viewing for the first time
} | null> {
  try {
    const axiosReq = await authAxios();

    const response = await axiosReq.get(
      `/companies/onboarding-state/${companyId}`
    );

    if (!response.data || !response.data.onboardingState) {
      throw new Error('No data in response');
    }

    return response.data.onboardingState;
  } catch (error) {
    console.error('Error getting company onboarding state:', error);
    return null;
  }
}

/**
 * Update company onboarding task completion
 * Sets task to 0 (incomplete) or 1 (complete)
 *
 * @param companyId - The company ID
 * @param taskId - The task ID to update
 * @param completed - Whether the task is completed
 * @returns Boolean indicating success
 */
export async function updateCompanyOnboardingTask(
  companyId: string,
  taskId: string,
  completed: boolean
): Promise<boolean> {
  try {
    const axiosReq = await authAxios();

    await axiosReq.post(`/companies/onboarding-task/${companyId}`, {
      taskId,
      completed,
    });

    return true;
  } catch (error) {
    console.error('Error updating company onboarding task:', error);
    return false;
  }
}

/**
 * Update company onboarding card state
 * Sets card state to 'open', 'minimized', or 'closed'
 *
 * @param companyId - The company ID
 * @param cardState - The card state to set
 * @returns Boolean indicating success
 */
export async function updateCompanyOnboardingCardState(
  companyId: string,
  cardState: 'open' | 'minimized' | 'closed'
): Promise<boolean> {
  try {
    const axiosReq = await authAxios();

    await axiosReq.post(`/companies/onboarding-card-state/${companyId}`, {
      cardState,
    });

    return true;
  } catch (error) {
    console.error('Error updating company onboarding card state:', error);
    return false;
  }
}

/**
 * Update confetti shown flag in onboarding state
 * Sets confettiShown to true or false
 *
 * @param companyId - The company ID
 * @param confettiShown - Whether confetti has been shown
 * @returns Boolean indicating success
 */
export async function updateOnboardingConfettiShown(
  companyId: string,
  confettiShown: boolean
): Promise<boolean> {
  try {
    const axiosReq = await authAxios();

    await axiosReq.post(`/companies/onboarding-confetti-shown/${companyId}`, {
      confettiShown,
    });

    return true;
  } catch (error) {
    console.error('Error updating confetti shown flag:', error);
    return false;
  }
}

/**
 * Sync onboarding tasks with feature_usage
 * Forces refresh of all onboarding task completion states derived from
 * feature_usage documents (including download-app)
 * based on actual feature usage, overriding any manual user changes
 *
 * @param companyId - The company ID
 * @returns Updated onboarding state or null on error
 */
export async function syncOnboardingWithFeatureUsage(
  companyId: string
): Promise<{
  tasks: {
    'import-transactions': number;
    'send-invoice': number;
    'upload-receipt': number;
    'chat-luca': number;
    'view-reports': number;
    'download-app': number;
  };
  cardState: 'open' | 'minimized' | 'closed';
  confettiShown?: boolean;
  isFirstTimeUser?: boolean;
} | null> {
  try {
    const axiosReq = await authAxios();

    const response = await axiosReq.post(
      `/companies/sync-onboarding/${companyId}`
    );

    if (!response.data || !response.data.onboardingState) {
      throw new Error('No data in response');
    }

    return response.data.onboardingState;
  } catch (error) {
    console.error('Error syncing onboarding with feature usage:', error);
    return null;
  }
}
